import { useCallback, useEffect, useState } from "react";
import { Button, Form, Input, Table, message, Modal, List } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { PlusCircleFilled } from "@ant-design/icons";

import DataTotalDisplay from "../../component/data-total-display/data-total-display.component";
import FormButtonsWrap from "../../component/form-buttons-wrap/form-buttons-wrap.component";

import { setMenu } from "../../redux/reducers/navigationSlice";
import { setUserAccount } from "../../redux/reducers/userAccountSlice";
import SchedulesGroupAPI from "../../api/SchedulesGroupAPI";
import SchedulesAPI from "../../api/SchedulesAPI";
import { weekDaysNumbersToESText } from "../../utils/dataUtils";

import "./schedules-group-maintenance.styles.scss";

const schedulesFormInitialValues = {
  id: "",
  name: "",
  scheduleId: "",
};

const SchedulesGroupMaintenancePage = () => {
  const [messageApi, messageContextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.userAccount.user.token);

  const [schedulesGroupData, setSchedulesGroupData] = useState({
    schedulesGroup: [],
    totalCount: 0,
  });
  const [schedules, setSchedules] = useState([]);
  const [selectedSchedules, setSelectedSchedules] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [isModalOpen, setModalOpen] = useState(false);
  const [scheduleModalIsOpen, setScheduleModalIsOpen] = useState(false);
  const [selectedId, setSelectedId] = useState("");

  const loadInitialData = useCallback(async () => {
    messageApi.open({ type: "loading", content: "Cargando..." });

    try {
      const schedulesGroupResponse = await SchedulesGroupAPI.getSchedulesGroup(
        token
      );
      const schedulesResponse = await SchedulesAPI.getSchedules(token);

      if (
        !schedulesGroupResponse.data.success ||
        !schedulesResponse.data.success
      ) {
        messageApi.open({
          type: "error",
          content: schedulesGroupResponse.data.message,
        });
        return;
      }

      setSchedulesGroupData(schedulesGroupResponse.data);
      const schedules = schedulesResponse.data.schedules ?? [];

      setSchedules(
        schedules.map((schedule) => ({
          key: schedule.key,
          Id: schedule.Id,
          Description: schedule.Description,
          WeekDays: weekDaysNumbersToESText(schedule.WeekDays, true),
          TimeRange: `${schedule.StartTime} a ${schedule.EndTime}`,
        }))
      );
      messageApi.destroy();
    } catch (err) {
      console.error(err.message);

      let message = "Servicio no disponible, intente más tarde. ";

      if (err.code !== "ERR_NETWORK") {
        message = err.response.data.message;

        if (err.response.status === 401) dispatch(setUserAccount(null));
      }

      messageApi.open({ type: "error", content: message });
    }
  }, [token, dispatch, messageApi]);

  const loadSchedulesGroupList = async (
    activePage = 1,
    activePageSize = 10
  ) => {
    messageApi.open({ type: "loading", content: "Cargando..." });

    try {
      const response = await SchedulesGroupAPI.getSchedulesGroup(token, {
        page: activePage,
        pageSize: activePageSize,
      });

      if (!response.data.success) {
        messageApi.open({
          type: "error",
          content: response.data.message,
        });
        return;
      }

      setSchedulesGroupData(response.data);
      messageApi.destroy();
    } catch (err) {
      console.error(err.message);

      let message = "Servicio no disponible, intente más tarde. ";

      if (err.code !== "ERR_NETWORK") {
        message = err.response.data.message;

        if (err.response.status === 401) dispatch(setUserAccount(null));
      }

      messageApi.open({ type: "error", content: message });
    }
  };

  const loadSelectedScheduleGroup = async (id) => {
    form.resetFields();
    messageApi.open({ type: "loading", content: "Cargando..." });

    try {
      const scheduleGroupResponse = await SchedulesGroupAPI.getSchedulesGroup(
        token,
        { id }
      );
      const scheduleGroupData = scheduleGroupResponse.data;
      const schedulesResponse = await SchedulesAPI.getSchedulesByGroup(
        token,
        id
      );
      const schedulesData = schedulesResponse.data;

      if (!scheduleGroupData.success) {
        messageApi.destroy();
        messageApi.open({
          type: "warning",
          content: scheduleGroupData.message,
        });
        return;
      }

      if (!schedulesData.success) {
        messageApi.destroy();
        messageApi.open({
          type: "warning",
          content: schedulesData.message,
        });
        return;
      }

      if (scheduleGroupData.schedulesGroup.length < 1) {
        messageApi.destroy();
        messageApi.open({ type: "error", content: "No encontrado" });
        return;
      }

      const { Id, Name, ScheduleId } = scheduleGroupData.schedulesGroup[0];

      form.setFieldsValue({
        id: Id,
        name: Name,
        scheduleId: ScheduleId,
      });

      setSelectedSchedules(
        schedulesData.schedules.map((schedule) => ({
          key: schedule.key,
          Id: schedule.Id,
          Description: schedule.Description,
          WeekDays: weekDaysNumbersToESText(schedule.WeekDays, true),
          TimeRange: `${schedule.StartTime} a ${schedule.EndTime}`,
        }))
      );

      messageApi.destroy();
    } catch (err) {
      console.error(err.message);

      let message = "Servicio no disponible, intente más tarde. ";

      if (err.code === "ERR_NETWORK") {
        message = err.response.data.message;

        if (err.response.status === 401) dispatch(setUserAccount(null));
      }

      messageApi.open({ type: "error", content: message });
    }
  };

  const onFinishScheduleGroupForm = async (values) => {
    const { id, name } = values;

    if (selectedSchedules.length < 1) {
      messageApi.open({
        type: "warning",
        content: "Debe seleccionar al menos un rango de horario.",
      });
      return;
    }

    try {
      let response = null;

      if (!id || isNaN(id)) {
        response = await SchedulesGroupAPI.createScheduleGroup(token, {
          name,
          selectedSchedules,
        });
      } else {
        response = await SchedulesGroupAPI.updateScheduleGroup(token, {
          id,
          name,
          selectedSchedules,
        });
      }

      const responseBody = response.data;

      form.resetFields();
      setSelectedSchedules([]);
      messageApi.open({
        type: responseBody.success ? "success" : "error",
        content: responseBody.message,
      });
      loadSchedulesGroupList();
    } catch (err) {
      console.error(err.message);

      let message = "Servicio no disponible, intente más tarde. ";

      if (err.code === "ERR_NETWORK") {
        message = err.response.data.message;

        if (err.response.status === 401) dispatch(setUserAccount(null));
      }

      messageApi.open({ type: "error", content: message });
    }
  };

  const onDeleteSchedule = async (id) => {
    try {
      const response = await SchedulesGroupAPI.deleteSchedule(token, id);
      const responseBody = response.data;

      form.resetFields();
      messageApi.open({
        type: responseBody.success ? "success" : "error",
        content: responseBody.message,
      });
      loadSchedulesGroupList();
    } catch (err) {
      console.error(err.message);

      let message = "Servicio no disponible, intente más tarde. ";

      if (err.code === "ERR_NETWORK") {
        message = err.response.data.message;

        if (err.response.status === 401) dispatch(setUserAccount(null));
      }

      messageApi.open({ type: "error", content: message });
    }
  };

  /**
   * Agrega el rango de horario seleccionado, a los que serán agregados al grupo.
   * @param {{key: number, Id: number, Description: string, WeekDays: string, TimeRange: string}} row - fila de información correspondiente a un rango horario
   */
  const onSelectSchedule = (row) => {
    if (selectedSchedules.some((schedule) => schedule.Id === row.Id)) {
      messageApi.warning("El horario ya se encuentra en la lista.");
      return;
    }

    setSelectedSchedules([...selectedSchedules, row]);
    setScheduleModalIsOpen(false);
  };

  /**
   * Remueve rango de horario del listado de grupo.
   * @param {number} id - Identificador de rango de horario
   */
  const onRemoveSelectedSchedule = (id) => {
    setSelectedSchedules(
      selectedSchedules.filter((schedule) => schedule.Id !== id)
    );
  };

  useEffect(() => {
    dispatch(setMenu("schedulesMaintenance"));
    loadInitialData();
  }, [dispatch, loadInitialData]);

  const schedulesGroupTableColumns = [
    { title: "Nombre", dataIndex: "Id" },
    {
      title: "Nombre",
      dataIndex: "Name",
    },
    {
      title: "Fecha Creación",
      dataIndex: "CreatedAt",
    },
    { title: "Fecha Actualización", dataIndex: "UpdatedAt" },
    {
      title: "Usuario",
      dataIndex: "UserName",
    },
    {
      title: "Opción",
      render: (_, { Id }) => (
        <FormButtonsWrap vertical>
          <Button onClick={() => loadSelectedScheduleGroup(Id)}>
            Seleccionar
          </Button>
        </FormButtonsWrap>
      ),
    },
  ];

  const schedulesFindTableColumns = [
    { title: "Descripción", dataIndex: "Description" },
    { title: "Días", dataIndex: "WeekDays" },
    { title: "Horario", dataIndex: "TimeRange", width: 170 },
    {
      title: "Opción",
      render: (_, row) => (
        <FormButtonsWrap vertical>
          <Button
            type="link"
            onClick={() => onSelectSchedule(row)}
            icon={<PlusCircleFilled />}
          >
            Agregar
          </Button>
        </FormButtonsWrap>
      ),
    },
  ];

  return (
    <div className="schedules-maintenance">
      {messageContextHolder}
      <Modal
        title="Confirmación de borrado"
        open={isModalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => {
          onDeleteSchedule(selectedId);
          setSelectedId("");
          setModalOpen(false);
        }}
      >
        <p>¿Desea continuar?</p>
      </Modal>
      <Modal
        width={800}
        title="Selecciona el horario que desees agregar al grupo"
        open={scheduleModalIsOpen}
        onCancel={() => setScheduleModalIsOpen(false)}
        footer={null}
      >
        <Table
          columns={schedulesFindTableColumns}
          dataSource={schedules}
          pagination={false}
        ></Table>
      </Modal>
      <div className="schedules-screen-column">
        <h1>Formulario</h1>
        <Form
          form={form}
          initialValues={schedulesFormInitialValues}
          onFinish={onFinishScheduleGroupForm}
        >
          <Form.Item type="text" name="id" style={{ display: "none" }}>
            <Input maxLength={11} />
          </Form.Item>
          <Form.Item label="Nombre" name="name" required>
            <Input type="text" maxLength={100} required />
          </Form.Item>
          <Form.Item>
            <label>Horarios: </label>
            <Button
              onClick={() => setScheduleModalIsOpen(true)}
              icon={<PlusCircleFilled />}
            >
              Agregar
            </Button>
          </Form.Item>
          <List
            style={{ marginBottom: "10px" }}
            header={<b>Horarios del grupo</b>}
            dataSource={selectedSchedules}
            bordered
            pagination={false}
            renderItem={(item) => (
              <List.Item>
                <div>
                  {item.Description} — ({item.WeekDays}) — {item.TimeRange}
                </div>
                <Button
                  type="link"
                  danger
                  onClick={() => onRemoveSelectedSchedule(item.Id)}
                >
                  Remover
                </Button>
              </List.Item>
            )}
          />
          <FormButtonsWrap>
            <Button type="primary" htmlType="submit">
              Guardar
            </Button>
            <Button
              onClick={() => {
                form.resetFields();
              }}
            >
              Limpiar
            </Button>
          </FormButtonsWrap>
        </Form>
      </div>
      <div className="schedules-screen-column">
        <h1>Grupos de Horario</h1>
        <DataTotalDisplay
          currentCount={schedulesGroupData.schedulesGroup.length}
          totalCount={schedulesGroupData.totalCount}
        />
        <Table
          columns={schedulesGroupTableColumns}
          dataSource={schedulesGroupData.schedulesGroup}
          pagination={{
            current: currentPage,
            pageSize: currentPageSize,
            total: schedulesGroupData.totalCount,
          }}
          onChange={async (e) => {
            await loadSchedulesGroupList(e.current, e.pageSize);
            setCurrentPage(e.current);
            setCurrentPageSize(e.pageSize);
          }}
        />
      </div>
    </div>
  );
};

export default SchedulesGroupMaintenancePage;
